// Kurs- og volummønstre («institusjonelle mønstre») og hjelpefunksjonene de bruker.
// Brukes både live (Signaler, Deep Dive) og i backtesten, så de må ikke se inn i fremtiden.

export function calcAVWAP(closes, highs, lows, volumes, anchorIdx) {
  if (!closes?.length || anchorIdx < 0 || anchorIdx >= closes.length) return [];
  let cumPV = 0, cumV = 0;
  return closes.map((c, i) => {
    if (i < anchorIdx || c == null) return null;
    const tp = ((highs[i] ?? c) + (lows[i] ?? c) + c) / 3;
    cumPV += tp * (volumes[i] || 0);
    cumV  += volumes[i] || 0;
    return cumV > 0 ? cumPV / cumV : null;
  });
}

export function calcRVOL(volumes, window = 20) {
  return volumes.map((v, i) => {
    if (i < window) return null;
    const avg = volumes.slice(i - window, i).reduce((s, x) => s + (x || 0), 0) / window;
    return avg > 0 ? v / avg : null;
  });
}

export function detectInstitutionalPatterns(closes, opens, highs, lows, volumes, timestamps) {
  const patterns = [];
  if (!closes?.length || !opens?.length) return patterns;
  const n = closes.length;
  const rvol = calcRVOL(volumes, 20);

  // EMA20
  let ema20 = null;
  const ema20s = closes.map((c, i) => {
    if (i === 0) { ema20 = c; return c; }
    ema20 = c * (2/21) + ema20 * (19/21);
    return ema20;
  });
  // MA50
  const ma50s = closes.map((_, i) => {
    if (i < 49) return null;
    return closes.slice(i-49, i+1).reduce((s,x)=>s+x,0)/50;
  });
  // MA200
  const ma200s = closes.map((_, i) => {
    if (i < 199) return null;
    return closes.slice(i-199, i+1).reduce((s,x)=>s+x,0)/200;
  });
  // RSI(14) array for divergence detection
  const rsiArr = (() => {
    const period = 14, arr = new Array(n).fill(null);
    if (n < period + 1) return arr;
    let ag = 0, al = 0;
    for (let i = 1; i <= period; i++) { const d = closes[i]-closes[i-1]; if(d>0) ag+=d; else al-=d; }
    ag /= period; al /= period;
    arr[period] = al === 0 ? 100 : 100 - 100/(1+ag/al);
    for (let i = period+1; i < n; i++) {
      const d = closes[i]-closes[i-1];
      ag = (ag*(period-1)+Math.max(d,0))/period;
      al = (al*(period-1)+Math.max(-d,0))/period;
      arr[i] = al === 0 ? 100 : 100 - 100/(1+ag/al);
    }
    return arr;
  })();

  for (let i = 5; i < n; i++) {
    const c = closes[i], o = opens[i], h = highs[i], l = lows[i];
    if (c == null || o == null || h == null || l == null) continue;
    const body = Math.abs(c - o);
    const lowerWick = Math.min(c, o) - l;
    const upperWick = h - Math.max(c, o);
    const range = h - l || 0.01;
    const rv = rvol[i];
    const dateStr = new Date(timestamps[i] * 1000).toISOString().slice(0, 10);
    const e20 = ema20s[i], m50 = ma50s[i], m200 = ma200s[i];
    const prevC = i > 0 ? closes[i - 1] : null;
    const afterDowntrend = i >= 5 && closes[i] < closes[i - 5] * 0.98;
    const afterUptrend   = i >= 5 && closes[i] > closes[i - 5] * 1.02;

    // 1. HØY RVOL
    if (rv !== null && rv >= 1.5) {
      if (c > o)
        patterns.push({ date: dateStr, type: "HIGH_RVOL_UP", strength: rv >= 2.5 ? "strong" : "moderate",
          rvol: rv, desc: `Høy RVOL (${rv.toFixed(1)}x) grønn dag — stor aktivitet, kan være kjøp eller distribusjon` });
      else
        patterns.push({ date: dateStr, type: "HIGH_RVOL_DOWN", strength: rv >= 2.5 ? "strong" : "moderate",
          rvol: rv, desc: `Høy RVOL (${rv.toFixed(1)}x) rød dag — stort salgspress eller panikkutsalg` });
    }

    // 2. BULL TRAP — svakt rally inn i MA-motstand (krever nærhet til MA for å redusere støy)
    const nearMAResistance = (e20 && c > e20*0.99 && c < e20*1.03) || (m50 && c > m50*0.99 && c < m50*1.03) || (m200 && c > m200*0.99 && c < m200*1.03);
    if (rv !== null && rv < 0.7 && prevC && c >= prevC * 1.01 && c > o && afterDowntrend && nearMAResistance) {
      patterns.push({ date: dateStr, type: "BULL_TRAP", strength: "moderate",
        desc: `Pris opp ${((c/prevC-1)*100).toFixed(1)}% på RVOL ${rv.toFixed(2)}x inn i MA-motstand — lav overbevisning, mulig bull trap` });
    }

    // 3. LIKVIDITETSJAKT
    const sweptEMA  = e20  && l < e20  * 0.998 && c > e20;
    const sweptMA50 = m50  && l < m50  * 0.998 && c > m50;
    if ((sweptEMA || sweptMA50) && lowerWick > body * 1.5 && c > o) {
      patterns.push({ date: dateStr, type: "LIQUIDITY_SWEEP", strength: lowerWick / range > 0.5 ? "strong" : "moderate",
        desc: `Likviditetsjakt under ${sweptMA50 ? "MA50" : "EMA20"} — stop losses ble tatt, institusjonene kjøpte` });
    }

    // 4. HAMMER
    const bodySmall  = body < range * 0.35;
    const hammerShape = lowerWick >= body * 2 && upperWick <= Math.max(body * 0.5, range * 0.08) && lowerWick / range > 0.5;
    if (hammerShape && bodySmall && afterDowntrend && !patterns.find(p => p.date === dateStr && p.type === "LIQUIDITY_SWEEP")) {
      patterns.push({ date: dateStr, type: "HAMMER", strength: c >= o ? "strong" : "moderate",
        desc: `Hammer etter nedgang — kjøperne snudde markedet fullstendig` });
    }

    // 5. SHOOTING STAR
    const starShape = upperWick >= body * 2 && lowerWick <= Math.max(body * 0.5, range * 0.08) && upperWick / range > 0.5;
    if (starShape && bodySmall && afterUptrend) {
      patterns.push({ date: dateStr, type: "SHOOTING_STAR", strength: c < o ? "strong" : "moderate",
        desc: `Shooting Star etter oppgang — selgerne banket prisen ned fra dagshøy` });
    }

    // 6. REJECTION WICK — skjerpet: krever nær MA ELLER ekstra kraftig wick (>3x kropp)
    const nearMA = (e20 && Math.abs(l - e20)/e20 < 0.04) || (m50 && Math.abs(l - m50)/m50 < 0.04) || (m200 && Math.abs(l - m200)/m200 < 0.04);
    const veryStrong = lowerWick > body * 3.5 && lowerWick / range > 0.6;
    if (lowerWick > body * 2.5 && lowerWick > upperWick * 2 && lowerWick / range > 0.45 && c > o && (nearMA || veryStrong)) {
      if (!patterns.find(p => p.date === dateStr && (p.type === "LIQUIDITY_SWEEP" || p.type === "HAMMER"))) {
        patterns.push({ date: dateStr, type: "REJECTION_WICK", strength: veryStrong ? "strong" : "moderate",
          desc: `Rejection wick${nearMA ? " ved MA-nivå" : ""} — markedet testet lavere nivåer men ble avvist kraftig` });
      }
    }

    // 7. MA-BOUNCE
    if (e20 && l <= e20 * 1.005 && l >= e20 * 0.985 && c > e20 && c > o && (rv === null || rv > 0.7))
      patterns.push({ date: dateStr, type: "MA_BOUNCE_EMA20", strength: "moderate", desc: `Presis bounce på EMA20 — algoritmisk støtte bekreftet` });
    if (m50 && l <= m50 * 1.005 && l >= m50 * 0.985 && c > m50 && c > o)
      patterns.push({ date: dateStr, type: "MA_BOUNCE_MA50", strength: "strong", desc: `Presis bounce på MA50 — institusjonell kjøpsordre ved glidende snitt` });

    // 8. BREAKOUT 20-DAGERS HØY — ny N-dags høy på høy RVOL
    if (i >= 20 && rv !== null && rv >= 1.5 && c > o) {
      const high20 = Math.max(...closes.slice(i - 20, i));
      if (c > high20) {
        patterns.push({ date: dateStr, type: "BREAKOUT_20D", strength: rv >= 2.0 ? "strong" : "moderate",
          desc: `Breakout: ny 20-dagers høy ($${c.toFixed(2)}) på RVOL ${rv.toFixed(1)}x — momentum bekreftet av volum` });
      }
    }
  }

  // 9. AKKUMULERINGSSONE
  if (n >= 15) {
    const last15c = closes.slice(-15), last15h = highs.slice(-15), last15l = lows.slice(-15);
    const last15v = volumes.slice(-15);
    if (last15c.every(v => v != null) && last15h.every(v => v != null)) {
      const rangeH = Math.max(...last15h), rangeL = Math.min(...last15l);
      const rangePct = (rangeH - rangeL) / rangeL * 100;
      const firstHalfVol = last15v.slice(0, 7).reduce((s,x)=>s+(x||0),0)/7;
      const secHalfVol   = last15v.slice(8).reduce((s,x)=>s+(x||0),0)/7;
      if (rangePct < 6 && secHalfVol < firstHalfVol * 0.85) {
        const dateStr = new Date(timestamps[n-1] * 1000).toISOString().slice(0, 10);
        patterns.push({ date: dateStr, type: "ACCUMULATION", strength: rangePct < 4 ? "strong" : "moderate",
          desc: `Akkumulering: ${rangePct.toFixed(1)}% range siste 15 dager med synkende volum — institusjonell stille akkumulering` });
      }
    }
  }

  // 10. RSI-DIVERGENS (bullish) — pris lavere bunn men RSI høyere bunn.
  // En bunn på dag k er først kjent når dag k+1 er ferdig, så signalet dateres dit (ingen titt inn i fremtiden).
  if (n >= 30) {
    const swingLows = [];
    for (let i = 2; i < n; i++) {
      const k = i - 1;
      if (lows[k] == null || lows[k - 1] == null || lows[i] == null || rsiArr[k] === null) continue;
      if (!(lows[k] < lows[k - 1] && lows[k] <= lows[i] && rsiArr[k] < 50)) continue;
      const sw1 = swingLows.length ? swingLows[swingLows.length - 1] : null;
      if (sw1 && k - sw1.idx <= 60 && lows[k] < sw1.price && rsiArr[k] > sw1.rsi + 3) {
        const dateStr = new Date(timestamps[i] * 1000).toISOString().slice(0, 10);
        patterns.push({ date: dateStr, type: "RSI_DIV_BULL", strength: "strong",
          desc: `RSI-divergens: pris satte lavere bunn ($${lows[k].toFixed(2)}) men RSI steg (${sw1.rsi.toFixed(0)}→${rsiArr[k].toFixed(0)}) — salgspresset tørker ut` });
      }
      swingLows.push({ idx: k, price: lows[k], rsi: rsiArr[k] });
    }
  }

  return patterns;
}
